'use client';

import { useId } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/cn';
import { Select } from './input';

interface SectionNavItem {
  key: string;
  label: string;
  href: string;
}

export interface SectionNavGroup {
  label: string;
  items: SectionNavItem[];
}

interface SectionNavProps {
  /** Accessible name of the nav and of the phone picker. */
  label: string;
  groups: SectionNavGroup[];
  active: string;
  /**
   * Runs before the menu moves to another page; call `leave` to go ahead
   * (e.g. after confirming unsaved changes). New-tab clicks skip it.
   */
  guard?: (leave: () => void) => void;
}

/**
 * Vertical grouped page menu for a two-column surface (menu left, page right).
 * Items are real links, so Cmd-click, the back button and pasted URLs work.
 * Below `md` it collapses to a select that navigates on change.
 */
export function SectionNav({ label, groups, active, guard }: SectionNavProps) {
  const router = useRouter();
  const go = (href: string) => {
    const leave = () => router.push(href);
    if (guard) guard(leave);
    else leave();
  };
  const idPrefix = useId();
  const items = groups.flatMap((group) => group.items);
  const activeListed = items.some((item) => item.key === active);

  return (
    <div>
      <Select
        aria-label={label}
        className="md:hidden"
        value={activeListed ? active : ''}
        onChange={(event) => {
          const item = items.find((candidate) => candidate.key === event.target.value);
          if (item) go(item.href);
        }}
      >
        {activeListed ? null : (
          <option value="" disabled>
            Choose a page
          </option>
        )}
        {groups.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.items.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
      <nav aria-label={label} className="hidden flex-col gap-4 md:flex">
        {groups.map((group, index) => (
          <div key={group.label} role="group" aria-labelledby={`${idPrefix}-${index}`} className="flex flex-col gap-0.5">
            <div id={`${idPrefix}-${index}`} className="px-2.5 pb-1 text-xs font-medium text-zinc-400">
              {group.label}
            </div>
            {group.items.map((item) => {
              const isActive = item.key === active;
              return (
                <Link
                  key={item.key}
                  href={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={(event) => {
                    if (!guard || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                    event.preventDefault();
                    go(item.href);
                  }}
                  className={cn(
                    'corner-brand-sm flex h-8 items-center px-2.5 text-sm transition-colors pointer-coarse:h-11',
                    isActive
                      ? 'bg-accent/15 font-medium text-zinc-50'
                      : 'text-zinc-300 hover:bg-white/[0.04] hover:text-zinc-50'
                  )}
                >
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
    </div>
  );
}
